import { Product } from '../../../types/product';
import { parseCondition } from './valueMappers';
import { parseCustomShippingMethods } from './shippingMapper';

export type HeaderMap = Record<string, number> | null;

/**
 * Build a label -> column-index lookup from the CSV header row so that
 * fields are read by their header name rather than a hard-coded position.
 * This keeps importing correct even if the column order changes.
 */
export function buildHeaderMap(headerRow: string[]): HeaderMap {
  const map: Record<string, number> = {};
  headerRow.forEach((label, index) => {
    const key = label.trim();
    if (key && map[key] === undefined) {
      map[key] = index;
    }
  });
  return map;
}

// Fixed-position fallback for the (rare) case the CSV has no header row.
// Matches the layout produced by src/utils/csv/headers.ts.
const FALLBACK_INDEX: Record<string, number> = {
  'カテゴリ': 0,
  'タイトル': 1,
  '説明': 2,
  '開始価格': 3,
  '即決価格': 4,
  '個数': 5,
  '開催期間': 6,
  '終了時間': 7,
  '商品発送元の都道府県': 29,
  '送料負担': 31,
  '商品の状態': 36,
  '返品の可否': 38,
  '入札者評価制限': 40,
  '悪い評価の割合での制限': 41,
  '入札者認証制限': 42,
  '自動延長': 43,
  '自動再出品': 46,
  '自動値下げ': 47,
  '自動値下げ価格変更率': 48,
  '送料固定': 51,
  'ネコポス': 54,
  'ネコ宅急便コンパクト': 55,
  'ネコ宅急便': 56,
  'ゆうパケット': 57,
  'ゆうパック': 58,
  'ゆうパケットポストmini': 59,
  'ゆうパケットプラス': 60,
  '発送までの日数': 61
};

// First shipping-method column ("配送方法1"); 10 methods x 5 fields follow.
const SHIPPING_METHODS_START = 62;
const SHIPPING_METHODS_LENGTH = 50;

function field(fields: string[], header: HeaderMap, label: string): string {
  if (header && header[label] !== undefined) {
    return (fields[header[label]] ?? '').trim();
  }
  const fallback = FALLBACK_INDEX[label];
  if (fallback !== undefined) {
    return (fields[fallback] ?? '').trim();
  }
  return '';
}

function imageColumnIndex(header: HeaderMap, n: number): number {
  const label = `画像${n}`;
  if (header && header[label] !== undefined) {
    return header[label];
  }
  // Fallback: image columns start at index 9, each image followed by a comment.
  return 9 + (n - 1) * 2;
}

function parseShippingTime(value: string): Product['shippingTime'] {
  const map: Record<string, Product['shippingTime']> = {
    '1日～2日': '1-2',
    '2日～3日': '2-3',
    '3日～7日': '3-7'
  };
  return map[value] || '';
}

/**
 * Collect the image filenames referenced by a single product row.
 * The exporter records each product's real filenames in the 画像1..画像10
 * columns, so we read them straight from the row instead of guessing.
 */
export function extractImageFilenames(fields: string[], header: HeaderMap): string[] {
  const names: string[] = [];
  for (let i = 1; i <= 10; i++) {
    const value = (fields[imageColumnIndex(header, i)] ?? '').trim();
    if (value) {
      names.push(value);
    }
  }
  return names;
}

export function createProductFromRow(fields: string[], header: HeaderMap): Product {
  const isYes = (label: string) => field(fields, header, label) === 'はい';

  const shippingStart =
    header && header['配送方法1'] !== undefined ? header['配送方法1'] : SHIPPING_METHODS_START;

  const product: Partial<Product> = {
    categoryYahoo: field(fields, header, 'カテゴリ'),
    title: field(fields, header, 'タイトル'),
    description: field(fields, header, '説明'),
    startPrice: parseInt(field(fields, header, '開始価格')) || 0,
    buyItNowPrice: field(fields, header, '即決価格')
      ? parseInt(field(fields, header, '即決価格'))
      : undefined,
    quantity: parseInt(field(fields, header, '個数')) || 1,
    auctionDuration: parseInt(field(fields, header, '開催期間')) || 7,
    endTime: field(fields, header, '終了時間') || '22',
    images: [],
    thumbnailImages: [],
    shippingLocation: (field(fields, header, '商品発送元の都道府県') as Product['shippingLocation']) || '',
    shippingResponsibility: field(fields, header, '送料負担') === '出品者' ? 'seller' : 'buyer',
    shippingTime: parseShippingTime(field(fields, header, '発送までの日数')),
    condition: parseCondition(field(fields, header, '商品の状態')),
    returnsAccepted: field(fields, header, '返品の可否') === '返品可',
    ratingRestriction: isYes('入札者評価制限'),
    badRatingRestriction: isYes('悪い評価の割合での制限'),
    bidderAuthentication: isYes('入札者認証制限'),
    autoExtensionByBid: isYes('自動延長'),
    autoExtension: isYes('自動値下げ'),
    autoExtensionRate: parseInt(field(fields, header, '自動値下げ価格変更率')) || 5,
    autoExtensionCount: parseInt(field(fields, header, '自動再出品')) || 1,
    shippingLater: field(fields, header, '送料固定') === 'いいえ',
    shippingCOD: false,
    nekoposu: isYes('ネコポス'),
    nekoCompact: isYes('ネコ宅急便コンパクト'),
    nekoEazy: isYes('ネコ宅急便'),
    yuPacket: isYes('ゆうパケット'),
    yuPack: isYes('ゆうパック'),
    yuPacketMini: isYes('ゆうパケットポストmini'),
    yuPacketPlus: isYes('ゆうパケットプラス'),
    customShippingMethods: parseCustomShippingMethods(
      fields.slice(shippingStart, shippingStart + SHIPPING_METHODS_LENGTH)
    ),
    listingType: 'auction',
    isFeatured: false,
    featuredMinPrice: 0,
    recommendedCollection: false,
    recommendedCollectionRate: 0,
    itemNumber: '',
    itemNumber2: '',
    itemNumberPrefix: false,
    itemNumberSuffix: false,
    itemNumberSerial: false,
    memo: '',
    memoPrefix: false,
    memoSuffix: false,
    memoSerial: false
  };

  return product as Product;
}

/**
 * Backwards-compatible positional mapper (no header). Mirrors the fixed
 * exporter layout. Retained so existing callers keep working.
 */
export function createProductFromFields(fields: string[]): Product {
  return createProductFromRow(fields, null);
}
